import { apiClient } from './api';

function unwrapData<T>(res: { success: boolean; data?: T; message?: string; error?: string }, fallbackMsg: string): T {
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || res.error || fallbackMsg);
  }
  return res.data;
}

export type BasalamReadStatus = 'unread' | 'read' | '';

export type BasalamConversationRow = {
  id: string;
  basalamChatId: string;
  displayName: string;
  lastMessageText: string;
  lastMessageAt: string;
  unreadCount: number;
  chatType: string;
};

export type BasalamMessageRow = {
  id: string;
  basalamMessageId: string;
  direction: 'in' | 'out';
  text: string;
  contentType: string;
  createdAt: string;
  canEdit: boolean;
};

type PageResult<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function mapConversation(raw: Record<string, unknown>): BasalamConversationRow {
  return {
    id: String(raw.id ?? ''),
    basalamChatId: String(raw.basalamChatId ?? ''),
    displayName: String(raw.displayName ?? ''),
    lastMessageText: String(raw.lastMessageText ?? ''),
    lastMessageAt: String(raw.lastMessageAt ?? ''),
    unreadCount: Number(raw.unreadCount ?? 0),
    chatType: String(raw.chatType ?? ''),
  };
}

function mapMessage(raw: Record<string, unknown>): BasalamMessageRow {
  return {
    id: String(raw.id ?? ''),
    basalamMessageId: String(raw.basalamMessageId ?? ''),
    direction: (raw.direction === 'out' ? 'out' : 'in') as 'in' | 'out',
    text: String(raw.text ?? ''),
    contentType: String(raw.contentType ?? 'text'),
    createdAt: String(raw.createdAt ?? ''),
    canEdit: Boolean(raw.canEdit),
  };
}

export type BasalamProductRow = {
  id: string;
  basalamProductId: string;
  title: string;
  price: number;
  inventory: number;
  available: boolean;
  published: boolean;
  statusName: string;
  statusValue: number;
  imageUrl?: string;
};

export type BasalamImportResult = {
  product: { id: string; name?: string; slug?: string };
  imagesAdded: number;
  reviewsAdded: number;
  usersAdded: number;
};

export type BasalamAutoReplySettings = {
  enabled: boolean;
  allowlist: string[];
};

function mapProduct(raw: Record<string, unknown>): BasalamProductRow {
  return {
    id: String(raw.id ?? raw.basalamProductId ?? ''),
    basalamProductId: String(raw.basalamProductId ?? raw.id ?? ''),
    title: String(raw.title ?? ''),
    price: Number(raw.price ?? 0),
    inventory: Number(raw.inventory ?? 0),
    available: Boolean(raw.available),
    published: raw.published !== false,
    statusName: String(raw.statusName ?? ''),
    statusValue: Number(raw.statusValue ?? 0),
    imageUrl: raw.imageUrl ? String(raw.imageUrl) : undefined,
  };
}

export const basalamService = {
  async listProducts(params: { page?: number; limit?: number; q?: string; statuses?: string } = {}): Promise<PageResult<BasalamProductRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/basalam/products', { params });
    const page = unwrapData(res, 'خطا در دریافت محصولات باسلام');
    return {
      ...page,
      data: (page.data ?? []).map(mapProduct),
    };
  },
  async importProduct(payload: {
    basalamProductId: number | string;
    name: string;
    slug: string;
    sku: string;
    categoryId: number;
    price: number;
    stock: number;
    shortDescription?: string;
    description?: string;
    isActive?: boolean;
    imageUrl?: string;
  }): Promise<BasalamImportResult> {
    const res = await apiClient.post<BasalamImportResult>(
      '/admin/basalam/products/import',
      {
        ...payload,
        basalamProductId: Number(payload.basalamProductId),
        imageUrl: payload.imageUrl || undefined,
      },
      { timeout: 180000 }
    );
    return unwrapData(res, 'وارد کردن محصول باسلام ناموفق بود');
  },
  async syncProductPrices(productId: string): Promise<{
    price?: number;
    colors?: { name: string; price?: number | null }[];
    variants?: { name: string; price?: number | null }[];
  }> {
    const res = await apiClient.post<Record<string, unknown>>(
      `/admin/basalam/products/${productId}/sync-prices`,
      {},
      { timeout: 60000 },
    );
    return unwrapData(res, 'به‌روزرسانی قیمت از باسلام ناموفق بود');
  },
  async getAutoReplySettings(): Promise<BasalamAutoReplySettings> {
    const res = await apiClient.get<BasalamAutoReplySettings>('/admin/basalam/auto-reply-settings');
    const data = unwrapData(res, 'خطا در دریافت تنظیمات پاسخ خودکار');
    return {
      enabled: Boolean(data.enabled),
      allowlist: Array.isArray(data.allowlist) ? data.allowlist.map(String) : [],
    };
  },
  async setAutoReplyEnabled(enabled: boolean): Promise<BasalamAutoReplySettings> {
    const res = await apiClient.patch<BasalamAutoReplySettings>('/admin/basalam/auto-reply-settings', { enabled });
    const data = unwrapData(res, 'خطا در ذخیره تنظیمات');
    return {
      enabled: Boolean(data.enabled),
      allowlist: Array.isArray(data.allowlist) ? data.allowlist.map(String) : [],
    };
  },
  async listConversations(params: { page?: number; limit?: number; status?: BasalamReadStatus } = {}): Promise<PageResult<BasalamConversationRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/basalam/conversations', { params });
    const page = unwrapData(res, 'خطا در دریافت گفتگوها');
    return {
      ...page,
      data: (page.data ?? []).map(mapConversation),
    };
  },
  async getConversation(id: string): Promise<BasalamConversationRow> {
    const res = await apiClient.get<Record<string, unknown>>(`/admin/basalam/conversations/${id}`);
    return mapConversation(unwrapData(res, 'گفتگو یافت نشد'));
  },
  async listMessages(conversationId: string, params: { page?: number; limit?: number } = {}): Promise<PageResult<BasalamMessageRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>(`/admin/basalam/conversations/${conversationId}/messages`, { params });
    const page = unwrapData(res, 'خطا در دریافت پیام‌ها');
    return {
      ...page,
      data: (page.data ?? []).map(mapMessage),
    };
  },
  async reply(id: string, text: string): Promise<BasalamMessageRow> {
    const res = await apiClient.post<Record<string, unknown>>(`/admin/basalam/conversations/${id}/reply`, { text });
    return mapMessage(unwrapData(res, 'خطا در ارسال پاسخ'));
  },
  async editMessage(conversationId: string, messageId: string, text: string): Promise<BasalamMessageRow> {
    const res = await apiClient.patch<Record<string, unknown>>(
      `/admin/basalam/conversations/${conversationId}/messages/${messageId}`,
      { text }
    );
    return mapMessage(unwrapData(res, 'خطا در ویرایش پیام'));
  },
};
